import { useState, useCallback } from "react";
import { DropResult } from "react-beautiful-dnd";
import { LayoutConfig, LayoutRow, LayoutColumn, ColumnWidth, ColumnMobileConfig, ColumnMargin, LayoutSectionConfig } from "./types";
import { createColumn, createRow, generateId } from "./layoutUtils";
import { DEFAULT_LAYOUT, createRowFromPreset, ROW_PRESETS } from "./layoutDefaults";

export const useLayoutState = (initial?: LayoutConfig) => {
  const [rows, setRows] = useState<LayoutRow[]>(() => {
    if (initial && initial.rows && initial.rows.length > 0) {
      return initial.rows;
    }
    return DEFAULT_LAYOUT.rows;
  });

  const getConfig = useCallback((): LayoutConfig => ({ rows }), [rows]);

  // --- Row operations ---

  const addRow = useCallback((presetIndex?: number) => {
    const preset = presetIndex !== undefined ? ROW_PRESETS[presetIndex] : ROW_PRESETS[0];
    const newRow = createRowFromPreset(preset);
    setRows((prev) => [...prev, newRow]);
  }, []);

  const deleteRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
  }, []);

  const moveRow = useCallback((rowId: string, direction: "up" | "down") => {
    setRows((prev) => {
      const index = prev.findIndex((r) => r.id === rowId);
      if (index === -1) return prev;
      if (direction === "up" && index === 0) return prev;
      if (direction === "down" && index === prev.length - 1) return prev;

      const next = [...prev];
      const swapIndex = direction === "up" ? index - 1 : index + 1;
      [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
      return next;
    });
  }, []);

  const updateRowSettings = useCallback(
    (rowId: string, settings: { gap: number; marginTop: number; marginBottom: number }) => {
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...settings } : r)));
    },
    [],
  );

  // --- Column operations ---

  const addColumn = useCallback((rowId: string) => {
    const col = createColumn({ value: 1, unit: "fr" });
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, columns: [...r.columns, col] } : r)),
    );
  }, []);

  const deleteColumn = useCallback((rowId: string, columnId: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        if (r.columns.length <= 1) return r;
        return { ...r, columns: r.columns.filter((c) => c.id !== columnId) };
      }),
    );
  }, []);

  const updateColumnWidth = useCallback((rowId: string, columnId: string, width: ColumnWidth) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        return {
          ...r,
          columns: r.columns.map((c) => (c.id === columnId ? { ...c, width } : c)),
        };
      }),
    );
  }, []);

  const updateColumnMobileConfig = useCallback(
    (rowId: string, columnId: string, mobileConfig: ColumnMobileConfig) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== rowId) return r;
          return {
            ...r,
            columns: r.columns.map((c) => (c.id === columnId ? { ...c, mobileConfig } : c)),
          };
        }),
      );
    },
    [],
  );

  const updateColumnMargin = useCallback(
    (rowId: string, columnId: string, margin: ColumnMargin) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== rowId) return r;
          return {
            ...r,
            columns: r.columns.map((c) => (c.id === columnId ? { ...c, margin } : c)),
          };
        }),
      );
    },
    [],
  );

  // --- Section operations ---

  const addSection = useCallback(
    (rowId: string, columnId: string, section: LayoutSectionConfig) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== rowId) return r;
          return {
            ...r,
            columns: r.columns.map((c) =>
              c.id === columnId ? { ...c, sections: [...c.sections, section] } : c,
            ),
          };
        }),
      );
    },
    [],
  );

  const updateSection = useCallback(
    (rowId: string, columnId: string, sectionIndex: number, content: unknown) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== rowId) return r;
          return {
            ...r,
            columns: r.columns.map((c) => {
              if (c.id !== columnId) return c;
              return {
                ...c,
                sections: c.sections.map((s, i) => (i === sectionIndex ? { ...s, content } : s)),
              };
            }),
          };
        }),
      );
    },
    [],
  );

  const deleteSection = useCallback((rowId: string, columnId: string, sectionIndex: number) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        return {
          ...r,
          columns: r.columns.map((c) => {
            if (c.id !== columnId) return c;
            return { ...c, sections: c.sections.filter((_, i) => i !== sectionIndex) };
          }),
        };
      }),
    );
  }, []);

  // --- DnD ---

  const onDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const [srcRowId, srcColId] = source.droppableId.split(":");
    const [dstRowId, dstColId] = destination.droppableId.split(":");

    setRows((prev) => {
      const next = prev.map((r) => ({
        ...r,
        columns: r.columns.map((c) => ({ ...c, sections: [...c.sections] })),
      }));

      const srcRow = next.find((r) => r.id === srcRowId);
      const dstRow = next.find((r) => r.id === dstRowId);
      if (!srcRow || !dstRow) return prev;

      const srcCol = srcRow.columns.find((c) => c.id === srcColId);
      const dstCol = dstRow.columns.find((c) => c.id === dstColId);
      if (!srcCol || !dstCol) return prev;

      const [moved] = srcCol.sections.splice(source.index, 1);
      dstCol.sections.splice(destination.index, 0, moved);

      return next;
    });
  }, []);

  // --- Reset ---

  const resetToDefault = useCallback(() => {
    setRows(DEFAULT_LAYOUT.rows);
  }, []);

  const loadConfig = useCallback((config: LayoutConfig) => {
    if (config && config.rows && config.rows.length > 0) {
      setRows(config.rows);
    } else {
      setRows(DEFAULT_LAYOUT.rows);
    }
  }, []);

  return {
    rows,
    getConfig,
    addRow,
    deleteRow,
    moveRow,
    updateRowSettings,
    addColumn,
    deleteColumn,
    updateColumnWidth,
    updateColumnMobileConfig,
    updateColumnMargin,
    addSection,
    updateSection,
    deleteSection,
    onDragEnd,
    resetToDefault,
    loadConfig,
  };
};
